import React, { useEffect, useState, useMemo, useContext } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Label,
  Input,
  Container,
  Row,
  Col,
  Button,
  Tooltip,
} from "reactstrap";
import Select from "react-select";
import {
  REGEX,
  MAX_FILE_SIZE,
  SELECT_CUSTOM_STYLES,
} from "constants/CommonConstants";
import Avatar from "components/CommonComponents/Avatar";
import { useDispatch, useSelector } from "react-redux";
import {
  getChannelPartnerProfileDetails,
  getStates,
  getCities,
  addCity,
  updateChannelPartnerProfileDetails,
} from "action/channelPartner";
import CreatableSelect from "components/CommonComponents/CreatableSelect";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import AdminHeader from "components/Headers/AdminHeader";
import CommonModal from "components/CommonComponents/CommonModal";
import AddNewCityModal from "components/CommonComponents/AddNewCityModal";

const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/jpg"];

const ALLOWED_DOCUMENT_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
];

export const initialState = {
  organizationName: {
    value: "",
    originalValue: "",
    error: "",
  },
  contactPersonFirstName: {
    value: "",
    originalValue: "",
    error: "",
  },
  contactPersonLastName: {
    value: "",
    originalValue: "",
    error: "",
  },
  contactPersonEmail: {
    value: "",
    originalValue: "",
    error: "",
  },
  contactPersonMobile: {
    value: "",
    originalValue: "",
    error: "",
  },
  alternateMobileNumber: {
    value: "",
    originalValue: "",
    error: "",
  },
  gstNo: {
    value: "",
    originalValue: "",
    error: "",
  },
  panNo: {
    value: "",
    originalValue: "",
    error: "",
  },
  lineOne: {
    value: "",
    originalValue: "",
    error: "",
  },
  lineTwo: {
    value: "",
    originalValue: "",
    error: "",
  },
  landmark: {
    value: "",
    originalValue: "",
    error: "",
  },
  stateID: {
    value: "",
    originalValue: "",
    error: "",
  },
  cityID: {
    value: "",
    originalValue: "",
    error: "",
  },
  pincode: {
    value: "",
    originalValue: "",
    error: "",
  },

  companyLogo: {
    value: null,
    originalValue: null,
    isNew: false,
    error: "",
  },

  supportingDocuments: {
    value: [],
    originalValue: [],
    error: "",
  },
  deletecompanylogo: {
    value: false,
    originalValue: false,
    error: "",
  },
  deleteddocumentids: {
    value: [],
    originalValue: [],
    error: "",
  },
  status: {
    value: "ACTIVE",
    originalValue: "ACTIVE",
    error: "",
  },
  createdOn: {
    value: "",
    originalValue: "",
    error: "",
  },
};

function ChannelPartnerProfile() {
  const [isEditing, setIsEditing] = useState();
  const [channelPartnerData, setChannelPartnerData] = useState(initialState);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const toggle = () => setTooltipOpen(!tooltipOpen);
  const { notify } = useContext(NotificationContext);
  const dispatch = useDispatch();
  const states =
    useSelector((state) => state.channelPartner.states?.data?.states) || [];
  const cities =
    useSelector((state) => state.channelPartner.cities?.data?.cities) || [];
  const [showAddCityModal, setShowAddCityModal] = useState({
    isopen: false,
    stateName: null,
    stateID: null,
  });
  const [newCityName, setNewCityName] = useState("");
  const [showLoaderOnCity, setShowLoaderOnCity] = useState(false);
  const [originalChannelPartnerData, setOriginalChannelPartnerData] =
    useState(initialState);
  const [documentModal, setDocumentModal] = useState({
    isOpen: false,
    title: "",
    url: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
  });
  const [logoMenuOpen, setLogoMenuOpen] = useState(false);

  useEffect(() => {
    getStates(dispatch);
  }, [dispatch]);

  const fetchChannelPartnerProfile = async () => {
    try {
      setListState({
        isLoading: true,
        error: "",
      });
      const res = await getChannelPartnerProfileDetails();

      if (res?.code === 200) {
        const data = res?.data;
        const supportingDocuments =
          data?.userDocuments?.map((doc) => ({
            id: doc.ID,
            file: null,
            preview: doc.documentPath,
            name:
              doc.documentName ||
              doc.documentPath?.split("/").pop() ||
              "Document",
            type: doc.documentPath?.toLowerCase().endsWith(".pdf")
              ? "application/pdf"
              : "image",
          })) || [];

        const mappedData = {
          organizationName: {
            value: data?.organizationName || "",
            originalValue: data?.organizationName || "",
            error: "",
          },
          contactPersonFirstName: {
            value: data?.userID?.firstName || "",
            originalValue: data?.userID?.firstName || "",
            error: "",
          },
          contactPersonLastName: {
            value: data?.userID?.lastName || "",
            originalValue: data?.userID?.lastName || "",
            error: "",
          },
          contactPersonEmail: {
            value: data?.userID?.email || "",
            originalValue: data?.userID?.email || "",
            error: "",
          },
          contactPersonMobile: {
            value: data?.userID?.mobile || "",
            originalValue: data?.userID?.mobile || "",
            error: "",
          },
          alternateMobileNumber: {
            value: data?.alternateMobileNumber || "",
            originalValue: data?.alternateMobileNumber || "",
            error: "",
          },
          gstNo: {
            value: data?.gstNo || "",
            originalValue: data?.gstNo || "",
            error: "",
          },
          panNo: {
            value: data?.panNo || "",
            originalValue: data?.panNo || "",
            error: "",
          },
          lineOne: {
            value: data?.lineOne || "",
            originalValue: data?.lineOne || "",
            error: "",
          },
          lineTwo: {
            value: data?.lineTwo || "",
            originalValue: data?.lineTwo || "",
            error: "",
          },
          landmark: {
            value: data?.landmark || "",
            originalValue: data?.landmark || "",
            error: "",
          },
          stateID: {
            value: data?.city?.state?.ID ? String(data.city.state.ID) : "",
            originalValue: data?.city?.state?.ID
              ? String(data.city.state.ID)
              : "",
            error: "",
          },
          cityID: {
            value: data?.city?.ID ? String(data.city.ID) : "",
            originalValue: data?.city?.ID ? String(data.city.ID) : "",
            error: "",
          },
          pincode: {
            value: data?.pincode || "",
            originalValue: data?.pincode || "",
            error: "",
          },
          companyLogo: {
            value: data?.companyLogoImageURL || null,
            originalValue: data?.companyLogoImageURL || null,
            isNew: false,
            error: "",
          },
          supportingDocuments: {
            value: supportingDocuments,
            originalValue: supportingDocuments,
            error: "",
          },
          deletecompanylogo: {
            value: false,
            originalValue: false,
            error: "",
          },
          deleteddocumentids: {
            value: [],
            originalValue: [],
            error: "",
          },
          status: {
            value: data?.userID?.status || "ACTIVE",
            originalValue: data?.userID?.status || "ACTIVE",
            error: "",
          },
          createdOn: {
            value: data?.userID?.createdOn || "",
            originalValue: data?.userID?.createdOn || "",
            error: "",
          },
        };
        setChannelPartnerData(mappedData);
        setOriginalChannelPartnerData(mappedData);
        if (data?.city?.state?.ID) {
          fetchCitiesForState(data.city.state.ID);
        }
        setListState({
          isLoading: false,
          error: "",
        });
      } else {
        setListState({
          isLoading: false,
          error: res?.message || "Failed to load Channel Partner details",
        });
      }
    } catch (error) {
      setListState({
        isLoading: false,
        error:
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while fetching Channel Partner",
      });
    }
  };

  useEffect(() => {
    fetchChannelPartnerProfile();
  }, []);

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    switch (name) {
      case "organizationName":
        return !trimmedValue
          ? "Please enter organization name"
          : !REGEX.ALPHABETS_NUM_SPACE_SPECIAL_SYMBOLS_ALLOWED.test(
                trimmedValue,
              )
            ? "Organization name contains invalid characters"
            : "";

      case "contactPersonFirstName":
        return !trimmedValue
          ? "Please enter first name"
          : !REGEX.ONLY_ALPHABETS_REGEX.test(trimmedValue)
            ? "Contact person name must contain only letters and spaces"
            : "";

      case "contactPersonLastName":
        return !trimmedValue
          ? "Please enter last name"
          : !REGEX.ONLY_ALPHABETS_REGEX.test(trimmedValue)
            ? "Contact person name must contain only letters and spaces"
            : "";

      case "gstNo":
        return !trimmedValue
          ? "Please enter GST number"
          : !REGEX.GST_REGEX.test(trimmedValue)
            ? "Invalid GST number"
            : "";

      case "panNo":
        return !trimmedValue
          ? "Please enter PAN number"
          : !REGEX.PAN_REGEX.test(trimmedValue)
            ? "Invalid PAN number"
            : "";

      case "lineOne":
        return !trimmedValue
          ? "Please enter address line 1"
          : trimmedValue.length > 150
            ? "Address line 1 cannot exceed 150 characters"
            : "";

      case "lineTwo":
        return trimmedValue && trimmedValue.length > 150
          ? "Address line 2 cannot exceed 150 characters"
          : "";

      case "landmark":
        return trimmedValue && trimmedValue.length > 100
          ? "Landmark cannot exceed 100 characters"
          : "";

      case "companyLogo":
        if (!value || typeof value === "string") return "";

        if (!ALLOWED_LOGO_TYPES.includes(value.type)) {
          return "Only PNG, JPG and JPEG files are allowed.";
        }

        if (value.size > MAX_FILE_SIZE) {
          return `Company Logo should not exceed ${
            MAX_FILE_SIZE / (1024 * 1024)
          } MB.`;
        }

        return "";

      case "supportingDocuments":
        for (const file of value) {
          if (file.file) {
            if (!ALLOWED_DOCUMENT_TYPES.includes(file.file.type)) {
              return "Only PDF, PNG, JPG and JPEG files are allowed.";
            }

            if (file.file.size > MAX_FILE_SIZE) {
              return `Each document should not exceed ${
                MAX_FILE_SIZE / (1024 * 1024)
              } MB.`;
            }
          }
        }

        return "";

      case "stateID":
        return !value ? "Please select state" : "";

      case "cityID":
        return !value ? "Please select city" : "";

      case "pincode":
        return !value
          ? "Please enter pincode"
          : value.length !== 6
            ? "Pincode must be 6 digits"
            : "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    let newChannelPartner = { ...channelPartnerData };
    Object.keys(newChannelPartner).forEach((key) => {
      if (key === "companyLogo") {
        newChannelPartner[key] = {
          ...newChannelPartner[key],
          error: "",
        };

        return;
      }

      newChannelPartner[key] = {
        ...newChannelPartner[key],
        error: getValidationError(key, newChannelPartner[key].value),
      };
    });

    setChannelPartnerData(newChannelPartner);

    return Object.values(newChannelPartner).every((item) => item.error === "");
  };

  const getEditedBodyData = () => {
    const editedData = {};

    const fieldsToCheck = [
      "organizationName",
      "contactPersonFirstName",
      "contactPersonLastName",
      "contactPersonEmail",
      "contactPersonMobile",
      "alternateMobileNumber",
      "gstNo",
      "panNo",
      "lineOne",
      "lineTwo",
      "landmark",
      "stateID",
      "cityID",
      "pincode",
      "status",
    ];

    fieldsToCheck.forEach((field) => {
      const currentValue = channelPartnerData[field]?.value;
      const originalValue = channelPartnerData[field]?.originalValue;

      if (currentValue !== originalValue) {
        editedData[field] = currentValue;
      }
    });

    return editedData;
  };
  const handleSave = async () => {
    if (!validateForm()) return;

    const editedData = getEditedBodyData();

    const hasLogoChange =
      channelPartnerData.companyLogo?.isNew === true ||
      channelPartnerData.deletecompanylogo?.value === true;

    const hasDocumentChange =
      (channelPartnerData.deleteddocumentids?.value?.length || 0) > 0 ||
      (channelPartnerData.supportingDocuments?.value || []).some(
        (item) => item.file instanceof File,
      );

    if (
      Object.keys(editedData).length === 0 &&
      !hasLogoChange &&
      !hasDocumentChange
    ) {
      notify("info", "No changes detected to update.");
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);

      const documents = channelPartnerData.supportingDocuments?.value || [];

      const newDocuments = documents
        .filter((item) => item.file instanceof File)
        .map((item) => item.file);

      const deletedDocumentIds =
        channelPartnerData.deleteddocumentids?.value || [];

      const payload = {
        organizationName: channelPartnerData.organizationName?.value || "",
        contactPersonFirstName:
          channelPartnerData.contactPersonFirstName?.value || "",
        contactPersonLastName:
          channelPartnerData.contactPersonLastName?.value || "",
        contactPersonEmail: channelPartnerData.contactPersonEmail?.value || "",
        contactPersonMobile:
          channelPartnerData.contactPersonMobile?.value || "",
        alternateMobileNumber:
          channelPartnerData.alternateMobileNumber?.value || "",
        gstNo: channelPartnerData.gstNo?.value || "",
        panNo: channelPartnerData.panNo?.value || "",
        lineOne: channelPartnerData.lineOne?.value || "",
        lineTwo: channelPartnerData.lineTwo?.value || "",
        landmark: channelPartnerData.landmark?.value || "",
        stateID: Number(channelPartnerData.stateID?.value),
        cityID: Number(channelPartnerData.cityID?.value),
        pincode: channelPartnerData.pincode?.value || "",
        deleteddocumentids: deletedDocumentIds,
      };

      const formData = new FormData();

      formData.append("channelpartner", JSON.stringify(payload));

      if (deletedDocumentIds.length > 0) {
        formData.append(
          "deleteddocumentids",
          JSON.stringify(deletedDocumentIds),
        );
      }

      const companyLogoData = channelPartnerData.companyLogo?.value;

      if (
        channelPartnerData.companyLogo?.isNew &&
        companyLogoData?.file instanceof File
      ) {
        formData.append("companylogo", companyLogoData.file);
      }

      newDocuments.forEach((file) => {
        formData.append("companydocument", file);
      });

      formData.append(
        "deletecompanylogo",
        channelPartnerData.deletecompanylogo?.value ? "true" : "false",
      );

      const res = await updateChannelPartnerProfileDetails(formData);

       if (res?.code === 200) {
        notify(
          "success",
          res?.message || "Channel Partner updated successfully",
        );

        await fetchChannelPartnerProfile();
        setIsEditing(false);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || err.message).join(", ")
          : typeof res?.message === "object"
            ? res.message.Msg || "Failed to update Channel Partner"
            : res?.message || "Failed to update Channel Partner";

        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while updating",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleOnCancel = () => {
    setLogoMenuOpen(false);
    setChannelPartnerData(originalChannelPartnerData);
    setIsEditing(false);
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setChannelPartnerData((prev) => ({
      ...prev,
      [name]: { ...prev[name], value: trimmedValue, error },
    }));
  };

  const handleSupportingDocsChange = (e) => {
    const files = Array.from(e.target.files);

    const newFiles = files
      .filter(
        (file) =>
          file.type.startsWith("image/") || file.type === "application/pdf",
      )
      .map((file) => ({
        file,
        preview: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : null,
        name: file.name,
        type: file.type,
      }));

    setChannelPartnerData((prev) => ({
      ...prev,
      supportingDocuments: {
        value: [...prev.supportingDocuments.value, ...newFiles].slice(0, 5),
        error: "",
      },
    }));

    e.target.value = "";
  };
  const handleImageChange = (file) => {
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setChannelPartnerData((prev) => ({
        ...prev,
        companyLogo: {
          ...prev.companyLogo,
          value: null,
          isNew: false,
          error: "Image should not exceed max size of 2 MB",
        },
      }));

      return;
    }

    setChannelPartnerData((prev) => ({
      ...prev,
      companyLogo: {
        ...prev.companyLogo,
        value: {
          file,
          preview: URL.createObjectURL(file),
          name: file.name,
          type: file.type,
        },
        isNew: true,
        error: "",
      },
    }));
  };
  const handleEditLogoClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLogoMenuOpen((prev) => !prev);
  };

  const handleChangeLogo = () => {
    setLogoMenuOpen(false);
    document.getElementById("profilePhotoInput").click();
  };
  const handleInputChange = (field, value) => {
    setChannelPartnerData((prev) => {
      const updatedState = {
        ...prev,
        [field]: {
          ...prev[field],
          value,
          error: "",
        },
      };

      if (field === "stateID") {
        updatedState.cityID = { ...prev.cityID, value: "", error: "" };
        updatedState.pincode = { ...prev.pincode, value: "", error: "" };
      }

      if (field === "cityID") {
        updatedState.pincode = { ...prev.pincode, value: "", error: "" };
      }

      if (field === "contactPersonMobile" || field === "pincode") {
        if (!REGEX.ONLY_DIGITS_REGEX.test(value)) {
          return {
            ...prev,
          };
        }

        return {
          ...prev,
          [field]: {
            ...prev[field],
            value,
            error: "",
          },
        };
      }

      return updatedState;
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
    setLogoMenuOpen(false);
  };
  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLogoMenuOpen(false);
    setChannelPartnerData((prev) => {
      const updatedData = {
        ...prev,
        companyLogo: {
          ...prev.companyLogo,
          value: null,
          isNew: false,
          error: "",
        },
        deletecompanylogo: {
          ...prev.deletecompanylogo,
          value: true,
        },
      };
      return updatedData;
    });
  };

  const stateOptions = useMemo(() => {
    return states.map((state) => ({
      value: String(state.ID),
      label: state.name,
    }));
  }, [states]);

  const cityOptions = useMemo(() => {
    return cities.map((city) => ({
      value: String(city.ID),
      label: city.name,
    }));
  }, [cities]);

  const fetchCitiesForState = (stateID) => {
    if (!stateID) return;

    const cityFilter = JSON.stringify([
      {
        field: "StateID",
        condition: "eq",
        filterValues: [String(stateID)],
      },
    ]);

    getCities(dispatch, cityFilter);
  };

  const handleOpenCityModal = () => {
    const stateID = channelPartnerData.stateID?.value;

    setShowAddCityModal({
      isopen: true,
      stateID: stateID ? Number(stateID) : null,
      stateName:
        stateOptions.find((state) => state.value === String(stateID))?.label ||
        null,
    });
  };

  const removeDocument = (index) => {
    setChannelPartnerData((prev) => {
      const documents = prev.supportingDocuments?.value || [];
      const documentToRemove = documents[index];

      const updatedDocuments = documents.filter((_, i) => i !== index);

      const deletedIds = [...(prev.deleteddocumentids?.value || [])];

      if (documentToRemove?.id) {
        deletedIds.push(documentToRemove.id);
      }

      return {
        ...prev,
        supportingDocuments: {
          ...prev.supportingDocuments,
          value: updatedDocuments,
          error: "",
        },
        deleteddocumentids: {
          ...prev.deleteddocumentids,
          value: deletedIds,
          error: "",
        },
      };
    });
  };
  const openDocument = (url, title = "Document") => {
    setDocumentModal({
      isOpen: true,
      title,
      url,
    });
  };

  const closeDocument = () => {
    setDocumentModal({
      isOpen: false,
      title: "",
      url: "",
    });
  };

  const handleSaveNewCity = async (stateID) => {
    setShowLoaderOnCity(true);

    try {
      const payload = {
        stateID: Number(stateID),
        name: newCityName.trim(),
        status: "ACTIVE",
      };
      const res = await addCity(payload);

      if (res?.code === 200) {
        notify("success", res?.message || "City added successfully");

        fetchCitiesForState(stateID);

        setShowAddCityModal({
          isopen: false,
          stateID: null,
          stateName: null,
        });

        setNewCityName("");
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => err.Msg || err.message || JSON.stringify(err))
              .join(", ")
          : res?.message || "Failed to add city";

        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to add city",
      );
    } finally {
      setShowLoaderOnCity(false);
    }
  };
  const handleCancelAddCity = () => {
    setShowAddCityModal({ isopen: false, stateID: null, stateName: null });
    setNewCityName("");
  };

  return (
    <>
      {/* If ADMIN  view by id -channel partner  details if CHANNEL PARTNER - my profile */}
      <AdminHeader />
      {listState.isLoading ? (
        <div className="loaderContainer">
          <i className="fa fa-spinner fa-spin fa-2xl loader" />
        </div>
      ) : listState.error ? (
        <div className="loaderContainer error-style">
          <div className="error-icon">
            <i className="fa-solid fa-circle-exclamation"></i>
          </div>

          <span>{listState.error}</span>
        </div>
      ) : (
        <Container fluid className="mt--6">
          <Row
            className={`profile-business-row ${isEditing ? "is-editing" : ""}`}
          >
            <Col md="4" className="profile-card-col">
              <Card
                className={`section-cards w-100 profile-card ${
                  isEditing ? "editing-card" : ""
                }`}
              >
                <CardBody className="text-center">
                  <FormGroup className="profile-photo-container text-center mb-1">
                    <div className="d-inline-block position-relative mt-3 profile-image-wrapper">
                      {channelPartnerData.companyLogo?.isNew &&
                      channelPartnerData.companyLogo?.value?.preview ? (
                        <img
                          src={channelPartnerData.companyLogo.value.preview}
                          alt="profilePhoto"
                          className="profile-photo-preview"
                        />
                      ) : channelPartnerData.companyLogo?.value ? (
                        <img
                          src={channelPartnerData.companyLogo.value}
                          alt="profilePhoto"
                          className="profile-photo-preview"
                        />
                      ) : channelPartnerData.companyLogo?.originalValue &&
                        !channelPartnerData.deletecompanylogo?.value ? (
                        <img
                          src={channelPartnerData.companyLogo.originalValue}
                          alt="profilePhoto"
                          className="profile-photo-preview"
                        />
                      ) : (
                        <Avatar
                          name={
                            channelPartnerData.organizationName?.value || " "
                          }
                        />
                      )}{" "}
                      {isEditing && (
                        <>
                          <span
                            className="profile-img-edit cursor"
                            onClick={handleEditLogoClick}
                          >
                            <i className="fa fa-pencil" />
                          </span>

                          {logoMenuOpen && (
                            <div className="logo-action-menu">
                              <button
                                type="button"
                                className="logo-action-btn"
                                onClick={handleChangeLogo}
                              >
                                <i className="fa fa-camera text-primary" />
                                <span className="text-muted">Change Image</span>
                              </button>

                              {channelPartnerData.companyLogo?.value && (
                                <button
                                  type="button"
                                  className="logo-action-btn"
                                  onClick={handleRemove}
                                >
                                  <i className="fa fa-trash text-danger" />
                                  <span className="text-muted">
                                    Remove Image
                                  </span>
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      {/* Status Dot */}
                      <span
                        id="statusDot"
                        className={`status-dot ${
                          channelPartnerData.status?.value === "ACTIVE"
                            ? "status-active"
                            : channelPartnerData.status?.value === "INACTIVE"
                              ? "status-inactive"
                              : "status-default"
                        }`}
                      />
                      {/* Status Tooltip */}
                      <Tooltip
                        placement="top"
                        isOpen={tooltipOpen}
                        target="statusDot"
                        toggle={toggle}
                      >
                        {channelPartnerData.status?.value === "ACTIVE"
                          ? "Active"
                          : channelPartnerData.status?.value === "INACTIVE"
                            ? "Inactive"
                            : "Unknown"}
                      </Tooltip>
                    </div>

                    {isEditing && (
                      <>
                        <span className="text-muted small d-block mt-2">
                          Accepted formats: PNG, JPG. Maximum file size: 2 MB.
                        </span>

                        <Input
                          type="file"
                          id="profilePhotoInput"
                          accept="image/png,image/jpeg"
                          className="d-none"
                          onChange={(e) => {
                            const file = e.target.files?.[0];

                            if (file) {
                              handleImageChange(file);
                            }

                            e.target.value = "";
                          }}
                        />
                      </>
                    )}
                  </FormGroup>

                  <h1 className="mb-1 ">
                    {channelPartnerData.organizationName?.value ||
                      "Organization Name"}
                  </h1>
                  <Col md="12" className="mb-2">
                    <Label className="small-label text-muted mb-1">
                      Role :{" "}
                      {channelPartnerData.role?.value || "Channel Partner"}
                    </Label>
                  </Col>
                  <hr className="my-3" />
                  <Label className="small-label text-muted d-block mb-1">
                    Contact Person Name
                    {isEditing && <span className="text-danger"> *</span>}
                  </Label>
                  <div className="contact-details text-left">
                    <div className="contact-detail-row">
                      <span className="contact-icon">
                        <i className="fas fa-user text-muted" />
                      </span>
                      <span className="small-text text-wrapper">
                        {`${channelPartnerData.contactPersonFirstName?.value || ""} ${
                          channelPartnerData.contactPersonLastName?.value || ""
                        }`.trim() || "-"}
                      </span>
                    </div>

                    <div className="contact-detail-row">
                      <span className="contact-icon">
                        <i className="fas fa-envelope text-muted" />
                      </span>
                      <span className="small-text text-wrapper">
                        {channelPartnerData.contactPersonEmail?.value || "-"}
                      </span>
                    </div>

                    <div className="contact-detail-row">
                      <span className="contact-icon">
                        <i className="fas fa-phone text-muted" />
                      </span>
                      <span className="small-text text-wrapper">
                        {channelPartnerData.contactPersonMobile?.value
                          ? `+91 ${channelPartnerData.contactPersonMobile.value}`
                          : "-"}
                      </span>
                    </div>
                  </div>
                  <Label className="small-label text-muted mb-1">
                    Account Created On
                  </Label>
                  <p className="small-text">
                    {channelPartnerData.createdOn?.value || "-"}
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col md="8" className="business-card-col">
              <Card
                className={`section-cards w-100 business-card ${
                  isEditing ? "editing-card" : ""
                }`}
              >
                <CardHeader className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center section-header">
                  <h4 className="mb-2 mb-sm-0">Business & Compliance</h4>

                <div className="mr-0 mr-sm-3">
                    {!isEditing ? (
                      <Button
                        color="primary"
                        className="btn-deep-blue"
                        onClick={handleEdit}
                      >
                        <i className="fa-solid fa-pencil mr-1"></i>
                        Edit
                      </Button>
                    ) : (
                      <>
                        <Button
                          color="primary"
                          className="btn-deep-blue px-4 py-2 mb-2 mb-sm-0"
                          type="button"
                          onClick={handleSave}
                        >
                          Save
                        </Button>

                        <Button
                          color="secondary"
                            className="px-4 py-2 ml-2"
                          type="button"
                          onClick={handleOnCancel}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardBody>
                  <>
                    {/* Organization & Contact Person - Edit Mode */}
                    {isEditing && (
                      <Row>
                        {/* Organization Name */}{" "}
                        <Col md="4">
                          <FormGroup className="d-flex flex-column align-items-start">
                            {" "}
                            <Label className="small-label text-muted mb-2">
                              Organization Name{" "}
                              <span className="text-danger"> *</span>{" "}
                            </Label>
                            <Input
                              type="text"
                              name="organizationName"
                              placeholder="Enter organization name"
                              value={
                                channelPartnerData.organizationName?.value || ""
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  "organizationName",
                                  e.target.value,
                                )
                              }
                              onBlur={(e) =>
                                handleOnBlur(e.target.name, e.target.value)
                              }
                              className={
                                channelPartnerData.organizationName?.error
                                  ? "is-invalid"
                                  : ""
                              }
                            />
                            {channelPartnerData.organizationName?.error && (
                              <div className="invalid-feedback d-block">
                                {channelPartnerData.organizationName.error}
                              </div>
                            )}
                          </FormGroup>
                        </Col>
                        {/* Contact Person First Name */}
                        <Col md="4">
                          <FormGroup className="d-flex flex-column align-items-start">
                            <Label className="small-label text-muted mb-2">
                              Contact Person First Name
                              <span className="text-danger"> *</span>
                            </Label>

                            <Input
                              type="text"
                              name="contactPersonFirstName"
                              placeholder="Enter first name"
                              value={
                                channelPartnerData.contactPersonFirstName
                                  ?.value || ""
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  "contactPersonFirstName",
                                  e.target.value,
                                )
                              }
                              onBlur={(e) =>
                                handleOnBlur(e.target.name, e.target.value)
                              }
                              className={
                                channelPartnerData.contactPersonFirstName?.error
                                  ? "is-invalid"
                                  : ""
                              }
                            />

                            {channelPartnerData.contactPersonFirstName
                              ?.error && (
                              <div className="invalid-feedback d-block">
                                {
                                  channelPartnerData.contactPersonFirstName
                                    .error
                                }
                              </div>
                            )}
                          </FormGroup>
                        </Col>
                        {/* Contact Person Last Name */}
                        <Col md="4">
                          <FormGroup className="d-flex flex-column align-items-start">
                            <Label className="small-label text-muted mb-2">
                              Contact Person Last Name
                              <span className="text-danger"> *</span>
                            </Label>

                            <Input
                              type="text"
                              name="contactPersonLastName"
                              placeholder="Enter last name"
                              value={
                                channelPartnerData.contactPersonLastName
                                  ?.value || ""
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  "contactPersonLastName",
                                  e.target.value,
                                )
                              }
                              onBlur={(e) =>
                                handleOnBlur(e.target.name, e.target.value)
                              }
                              className={
                                channelPartnerData.contactPersonLastName?.error
                                  ? "is-invalid"
                                  : ""
                              }
                            />

                            {channelPartnerData.contactPersonLastName
                              ?.error && (
                              <div className="invalid-feedback d-block">
                                {channelPartnerData.contactPersonLastName.error}
                              </div>
                            )}
                          </FormGroup>
                        </Col>
                      </Row>
                    )}

                    <Row>
                      <Col md="6">
                        <FormGroup className="d-flex flex-column align-items-start">
                          <Label className="small-label text-muted mb-2">
                            GST Number
                            {isEditing && (
                              <span className="text-danger"> *</span>
                            )}
                          </Label>

                          {isEditing ? (
                            <>
                              <Input
                                name="gstNo"
                                value={channelPartnerData.gstNo?.value || ""}
                                onChange={(e) =>
                                  handleInputChange("gstNo", e.target.value)
                                }
                                onBlur={(e) =>
                                  handleOnBlur(e.target.name, e.target.value)
                                }
                                className={
                                  channelPartnerData.gstNo?.error
                                    ? "is-invalid"
                                    : ""
                                }
                              />

                              {channelPartnerData.gstNo?.error && (
                                <div className="invalid-feedback d-block">
                                  {channelPartnerData.gstNo.error}
                                </div>
                              )}
                            </>
                          ) : (
                            <small className="small-text">
                              {channelPartnerData.gstNo?.value || "-"}
                            </small>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup className="d-flex flex-column align-items-start">
                          <Label className="small-label text-muted mb-2">
                            PAN Number
                            {isEditing && (
                              <span className="text-danger"> *</span>
                            )}
                          </Label>

                          {isEditing ? (
                            <>
                              <Input
                                type="text"
                                name="panNo"
                                placeholder="Enter PAN number"
                                value={channelPartnerData.panNo?.value || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    "panNo",
                                    e.target.value.toUpperCase(),
                                  )
                                }
                                onBlur={(e) =>
                                  handleOnBlur(e.target.name, e.target.value)
                                }
                                className={
                                  channelPartnerData.panNo?.error
                                    ? "is-invalid"
                                    : ""
                                }
                              />

                              {channelPartnerData.panNo?.error && (
                                <div className="invalid-feedback d-block">
                                  {channelPartnerData.panNo.error}
                                </div>
                              )}
                            </>
                          ) : (
                            <small className="small-text">
                              {channelPartnerData.panNo?.value || "-"}
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="6">
                        <FormGroup className="d-flex flex-column align-items-start">
                          <Label className="small-label text-muted mb-2">
                            Address Line 1
                            {isEditing && (
                              <span className="text-danger"> *</span>
                            )}
                          </Label>

                          {isEditing ? (
                            <>
                              <Input
                                type="textarea"
                                name="lineOne"
                                value={channelPartnerData.lineOne?.value || ""}
                                onChange={(e) =>
                                  handleInputChange("lineOne", e.target.value)
                                }
                                onBlur={(e) =>
                                  handleOnBlur(e.target.name, e.target.value)
                                }
                                className={
                                  channelPartnerData.lineOne?.error
                                    ? "is-invalid"
                                    : ""
                                }
                                maxLength={300}
                              />

                              {channelPartnerData.lineOne?.error && (
                                <div className="invalid-feedback d-block">
                                  {channelPartnerData.lineOne.error}
                                </div>
                              )}
                            </>
                          ) : (
                            <small className="small-text">
                              {channelPartnerData.lineOne?.value || "-"}
                            </small>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup className="d-flex flex-column align-items-start">
                          <Label className="small-label text-muted mb-2">
                            Address Line 2
                          </Label>

                          {isEditing ? (
                            <Input
                              type="textarea"
                              name="lineTwo"
                              value={channelPartnerData.lineTwo?.value || ""}
                              onChange={(e) =>
                                handleInputChange("lineTwo", e.target.value)
                              }
                            />
                          ) : (
                            <small className="small-text">
                              {channelPartnerData.lineTwo?.value || "-"}
                            </small>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup className="d-flex flex-column align-items-start">
                          <Label className="small-label text-muted mb-2">
                            Landmark
                          </Label>

                          {isEditing ? (
                            <Input
                              type="text"
                              name="landmark"
                              value={channelPartnerData.landmark?.value || ""}
                              onChange={(e) =>
                                handleInputChange("landmark", e.target.value)
                              }
                            />
                          ) : (
                            <small className="small-text">
                              {channelPartnerData.landmark?.value || "-"}
                            </small>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup className="d-flex flex-column align-items-start">
                          <Label className="small-label text-muted mb-2">
                            State
                          </Label>

                          {isEditing ? (
                            <Select
                              options={stateOptions}
                              placeholder="Select state"
                              classNamePrefix="react-select"
                              value={
                                stateOptions.find(
                                  (option) =>
                                    option.value ===
                                    String(
                                      channelPartnerData.stateID?.value || "",
                                    ),
                                ) || null
                              }
                              onChange={(selected) => {
                                const value = selected?.value || "";
                                handleInputChange("stateID", value);
                                if (value) {
                                  fetchCitiesForState(value);
                                }
                              }}
                              styles={SELECT_CUSTOM_STYLES}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              isClearable
                            />
                          ) : (
                            <small className="small-text">
                              {stateOptions.find(
                                (option) =>
                                  option.value ===
                                  channelPartnerData.stateID?.value,
                              )?.label || "-"}
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="d-flex flex-column align-items-start">
                          <Label className="small-label text-muted mb-2">
                            City
                          </Label>

                          {isEditing ? (
                            <CreatableSelect
                              placeholder="Select city"
                              options={cityOptions}
                              value={channelPartnerData.cityID?.value || ""}
                              onChange={(selected) => {
                                handleInputChange(
                                  "cityID",
                                  selected ? String(selected.value) : "",
                                );
                              }}
                              isDisabled={!channelPartnerData.stateID?.value}
                              onAddNew={handleOpenCityModal}
                              classNamePrefix="react-select"
                              isClearable
                            />
                          ) : (
                            <small className="small-text">
                              <small className="small-text">
                                {cityOptions.find(
                                  (option) =>
                                    option.value ===
                                    String(
                                      channelPartnerData.cityID?.value || "",
                                    ),
                                )?.label || "-"}
                              </small>
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="d-flex flex-column align-items-start">
                          <Label className="small-label text-muted mb-2">
                            Pincode
                          </Label>

                          {isEditing ? (
                            <>
                              <Input
                                type="text"
                                name="pincode"
                                placeholder="Enter pincode"
                                value={channelPartnerData.pincode?.value || ""}
                                onChange={(e) =>
                                  handleInputChange("pincode", e.target.value)
                                }
                                onBlur={(e) =>
                                  handleOnBlur(e.target.name, e.target.value)
                                }
                                className={
                                  channelPartnerData.pincode?.error
                                    ? "is-invalid"
                                    : ""
                                }
                              />

                              {channelPartnerData.pincode?.error && (
                                <div className="invalid-feedback d-block">
                                  {channelPartnerData.pincode.error}
                                </div>
                              )}
                            </>
                          ) : (
                            <small className="small-text">
                              {channelPartnerData.pincode?.value || "-"}
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row className="profile-business-row">
            <Col>
              <Card className="section-cards w-100">
                <CardHeader className="section-header ">
                  <h4 className="mb-0 text-default section-title">Documents</h4>
                </CardHeader>
                <CardBody>
                  <FormGroup>
                    <Label className="form-control-label">
                      Supporting Documents
                    </Label>
                    <Input
                      type="file"
                      id="supportingDocs"
                      multiple
                      accept="image/*,application/pdf"
                      className="d-none"
                      onChange={handleSupportingDocsChange}
                    />

                    {isEditing && (
                      <span className="text-muted mt-1 d-block">
                        Accepted formats: PDF, PNG, JPG. Max 5 files
                      </span>
                    )}

                    {isEditing ? (
                      <div
                        className="product-image-upload w-100 p-3 border rounded text-center cursor"
                        onClick={(e) => {
                          e.stopPropagation();

                          document.getElementById("supportingDocs").click();
                        }}
                      >
                        {channelPartnerData.supportingDocuments?.value?.length >
                        0 ? (
                          <div className="d-flex flex-wrap align-items-start">
                            {channelPartnerData.supportingDocuments.value.map(
                              (item, index) => (
                                <div
                                  key={index}
                                  className="edit-doc-square mr-3 mb-3"
                                >
                                  <div className="edit-doc-preview">
                                    {item.type === "application/pdf" ? (
                                      <i className="fa fa-file-pdf text-danger fa-4x" />
                                    ) : (
                                      <img
                                        src={item.preview}
                                        className="product-preview-img"
                                        alt={item.name}
                                      />
                                    )}
                                  </div>

                                  {/* Remove */}
                                  <span
                                    className="edit-doc-remove"
                                    onClick={(e) => {
                                      e.stopPropagation();

                                      removeDocument(index);
                                    }}
                                  >
                                    &times;
                                  </span>
                                </div>
                              ),
                            )}

                            {channelPartnerData.supportingDocuments.value
                              .length < 5 && (
                              <div
                                className="add-more-container"
                                onClick={(e) => {
                                  e.stopPropagation();

                                  document
                                    .getElementById("supportingDocs")
                                    .click();
                                }}
                              >
                                <div className="add-more-file-wrapper">+</div>

                                <span className="small">Add more</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center">
                            <i className="fa fa-cloud-upload fa-2x text-muted mb-2" />

                            <p className="mb-1 text-muted">
                              Click to upload supporting documents
                            </p>

                            <span className="btn btn-sm btn-color">
                              Choose Files
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mb-3">
                        {channelPartnerData.supportingDocuments?.value?.length >
                        0 ? (
                          <div className="d-flex flex-wrap">
                            {channelPartnerData.supportingDocuments.value.map(
                              (item, index) => {
                                const isPdf =
                                  item.type === "application/pdf" ||
                                  item.preview
                                    ?.toLowerCase()
                                    .split("?")[0]
                                    .endsWith(".pdf");

                                return (
                                  <div
                                    key={item.id || index}
                                    className="doc-square mr-3 mb-3"
                                  >
                                    <div
                                      className="doc-preview cursor-pointer"
                                      onClick={() =>
                                        openDocument(
                                          item.preview,
                                          item.name ||
                                            `Supporting Document ${index + 1}`,
                                        )
                                      }
                                    >
                                      {isPdf ? (
                                        <div className="pdf-preview">
                                          <i className="fa fa-file-pdf text-danger fa-3x" />

                                          <div className="small mt-1">
                                            PDF Document
                                          </div>
                                        </div>
                                      ) : (
                                        <img
                                          src={item.preview}
                                          alt={item.name || "Document"}
                                          className="doc-image"
                                        />
                                      )}
                                    </div>

                                    <div className="doc-actions">
                                      {/* View */}
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="doc-action-btn"
                                        onClick={() =>
                                          openDocument(
                                            item.preview,
                                            item.name ||
                                              `Supporting Document ${index + 1}`,
                                          )
                                        }
                                      >
                                        <i className="fa fa-eye mr-1" />
                                      </Button>

                                      {/* Download */}
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="doc-action-btn"
                                        onClick={() => {
                                          const a = document.createElement("a");
                                          a.href = item.preview;
                                          a.download =
                                            item.name ||
                                            `document-${index + 1}`;
                                          a.target = "_blank";
                                          a.click();
                                        }}
                                      >
                                        <i className="fa fa-download mr-1" />
                                      </Button>
                                    </div>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        ) : (
                          <div className="doc-empty-state">
                            <i className="fa-regular fa-folder-open doc-empty-icon" />
                            <p className="doc-empty-title">
                              No documents available
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      )}
      {showAddCityModal && (
        <AddNewCityModal
          isOpen={showAddCityModal.isopen}
          stateID={showAddCityModal.stateID}
          stateName={showAddCityModal.stateName}
          cityName={newCityName}
          setCityName={setNewCityName}
          onSave={handleSaveNewCity}
          onCancel={handleCancelAddCity}
          showLoader={showLoaderOnCity}
        />
      )}
      <CommonModal
        isOpen={documentModal.isOpen}
        toggle={closeDocument}
        title={documentModal.title}
        size="xl"
        height="80vh"
      >
        {documentModal.url &&
          (documentModal.url.toLowerCase().split("?")[0].endsWith(".pdf") ? (
            <iframe
              src={documentModal.url}
              title={documentModal.title}
              width="100%"
              height="100%"
              className="document-preview-iframe"
            />
          ) : (
            <div className="text-center">
              <img
                src={documentModal.url}
                alt={documentModal.title}
                className="img-fluid document-preview-img modal-preview-media"
              />
            </div>
          ))}
      </CommonModal>
    </>
  );
}

export default ChannelPartnerProfile;
